// "Run" a TestAudioFile as a new thread.
//
// Loads the WAV into a WaveSurfer v7 instance for play/seek, lets the user
// pick a title and duration window, then POSTs /threads/bulk-import with
// the `test_audio_file_id` shape. The backend resolves the row, kicks off
// BulkImportWorkflow against the normalised WAV, and we navigate to the
// new thread's detail page so SSE refresh-on-event surfaces the pipeline
// progress.

import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import { getTestAudioFileAudioUrl } from "../services/testAudioService";
import { bulkImportThread } from "../services/embed";

function formatDuration(ms) {
  if (!ms || ms <= 0) return "0:00";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function isoTimestampLocal() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const RunTestAudioDialog = ({
  isOpen,
  file,
  onClose,
  // Test injection points.
  audioUrlFn,
  bulkImportFn,
  navigateFn,
  // The default WaveSurfer module is loaded lazily so jsdom never tries to
  // evaluate it during component tests.
  wavesurferFactory,
}) => {
  const toast = useToast();
  const _navigate = useNavigate();
  const navigate = navigateFn || _navigate;
  const doAudioUrl = audioUrlFn || getTestAudioFileAudioUrl;
  const doBulkImport = bulkImportFn || bulkImportThread;

  const waveContainerRef = useRef(null);
  const waveSurferRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioUrlError, setAudioUrlError] = useState(null);

  const [title, setTitle] = useState("");
  const [durationMs, setDurationMs] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const maxDurationMs = Number(file?.duration_ms || 0);
  const isReady = file?.status === "READY";

  // Initialise defaults whenever the dialog opens with a (new) file.
  useEffect(() => {
    if (!isOpen || !file) return;
    setTitle(`${file.name} - ${isoTimestampLocal()}`);
    setDurationMs(maxDurationMs);
    setError(null);
    setAudioUrl(null);
    setAudioUrlError(null);
    setIsPlaying(false);
  }, [isOpen, file, maxDurationMs]);

  // Fetch the presigned audio URL when the dialog opens with a READY file.
  useEffect(() => {
    if (!isOpen || !file || !isReady) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await doAudioUrl(file.id);
        if (!cancelled) setAudioUrl(resp?.url || null);
      } catch (e) {
        if (!cancelled) {
          setAudioUrlError(
            e?.response?.data?.detail || e?.message || "Could not load audio",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, file, isReady, doAudioUrl]);

  // Initialise WaveSurfer once we have a URL + a container. Lazily import
  // so jsdom never executes the WebAudio code path.
  useEffect(() => {
    if (!audioUrl || !waveContainerRef.current) return;
    let disposed = false;
    let ws = null;

    (async () => {
      try {
        const factory =
          wavesurferFactory ||
          (async () => (await import("wavesurfer.js")).default);
        const WaveSurfer = await factory();
        if (disposed) return;
        ws = WaveSurfer.create({
          container: waveContainerRef.current,
          waveColor: "#a0aec0",
          progressColor: "#3182ce",
          height: 64,
          url: audioUrl,
        });
        ws.on("play", () => setIsPlaying(true));
        ws.on("pause", () => setIsPlaying(false));
        ws.on("finish", () => setIsPlaying(false));
        waveSurferRef.current = ws;
      } catch {
        if (!disposed) {
          setAudioUrlError("Audio player failed to initialise.");
        }
      }
    })();

    return () => {
      disposed = true;
      try {
        ws?.destroy?.();
      } catch {
        /* noop */
      }
      waveSurferRef.current = null;
    };
  }, [audioUrl, wavesurferFactory]);

  const togglePlay = () => {
    const ws = waveSurferRef.current;
    if (!ws) return;
    try {
      if (ws.isPlaying?.()) ws.pause();
      else ws.play();
    } catch {
      /* noop */
    }
  };

  // Clamp the slider value to the file's natural duration.
  const handleSlider = (v) => {
    const clamped = Math.max(1, Math.min(Number(v) || 0, maxDurationMs));
    setDurationMs(clamped);
  };

  const canSubmit =
    isReady && !submitting && title.trim().length > 0 && durationMs > 0;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await doBulkImport({
        testAudioFileId: file.id,
        title: title.trim(),
        durationMs,
      });
      toast({
        title: "Thread created",
        description: "Bulk import has started.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose?.();
      navigate(`/threads/${created.thread_id}`);
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Bulk import failed.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={submitting ? () => {} : onClose} size="xl">
      <ModalOverlay />
      <ModalContent data-testid="run-test-audio-dialog">
        <ModalHeader>Run Test Audio</ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {file && (
              <Box>
                <Text fontSize="sm" color="gray.600">
                  {file.name} · {formatDuration(maxDurationMs)}
                </Text>
              </Box>
            )}

            {!isReady && file && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  This file is {file.status}. The Run action becomes available once status is READY.
                </Text>
              </Alert>
            )}

            {isReady && (
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={3}
                bg="gray.50"
                data-testid="wavesurfer-container-wrap"
              >
                <HStack spacing={3} align="center" mb={2}>
                  <IconButton
                    size="sm"
                    onClick={togglePlay}
                    aria-label={isPlaying ? "Pause" : "Play"}
                    icon={
                      <Text fontWeight="bold">{isPlaying ? "❚❚" : "▶"}</Text>
                    }
                    data-testid="run-play-toggle"
                  />
                  <Text fontSize="xs" color="gray.500">
                    {audioUrl
                      ? "Drag the waveform to seek."
                      : audioUrlError
                      ? audioUrlError
                      : "Loading audio…"}
                  </Text>
                </HStack>
                <Box
                  ref={waveContainerRef}
                  data-testid="wavesurfer-container"
                  minH="64px"
                />
              </Box>
            )}

            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                isDisabled={submitting}
                data-testid="run-title-input"
              />
            </FormControl>

            <FormControl
              isDisabled={!maxDurationMs || submitting}
              data-testid="run-duration-form"
            >
              <FormLabel>
                Duration to import: {formatDuration(durationMs)}
                {maxDurationMs ? (
                  <Text as="span" color="gray.500" fontSize="sm" ml={2}>
                    (max {formatDuration(maxDurationMs)})
                  </Text>
                ) : null}
              </FormLabel>
              <Slider
                min={1}
                max={maxDurationMs || 1}
                step={1000}
                value={durationMs}
                onChange={handleSlider}
                isDisabled={!maxDurationMs || submitting}
                aria-label="run-duration-slider"
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb data-testid="run-duration-thumb" />
              </Slider>
              <FormHelperText>
                Defaults to the full file. Drag to import only a prefix.
              </FormHelperText>
            </FormControl>

            {error && (
              <Alert status="error" borderRadius="md" data-testid="run-error">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            mr={3}
            onClick={onClose}
            variant="ghost"
            isDisabled={submitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreate}
            isDisabled={!canSubmit}
            isLoading={submitting}
            data-testid="run-create-thread-button"
          >
            Create Thread
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { formatDuration };
export default RunTestAudioDialog;
